import { Group, Rect, Text } from 'react-konva'
import { KonvaEventObject } from 'konva/lib/Node'
import {
  BaseElementBounds,
  BaseElementData,
} from '../../../models/Construction'
import { useElementBehaviors } from '../../../hooks/useElementBehaviors'
import {
  useElementStyling,
  ElementStyling,
} from '../../../hooks/useElementStyling'
import { ElementInteractionCallbacks, Point2D, ResizeMode } from './Elements'
import { useCallback, useRef, useState } from 'react'

// Context Menu Component
interface ContextMenuProps {
  x: number
  y: number
  isVisible: boolean
  onDelete: () => void
  onClose: () => void
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  x,
  y,
  isVisible,
  onDelete,
  onClose,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  if (!isVisible) return null

  return (
    <Group>
      {/* Background overlay to catch clicks outside menu */}
      <Rect
        x={-10000}
        y={-10000}
        width={20000}
        height={20000}
        fill='transparent'
        onClick={onClose}
      />

      {/* Context menu background */}
      <Rect
        x={x}
        y={y}
        width={80}
        height={30}
        fill='#FFFFFF'
        stroke='#CCCCCC'
        strokeWidth={1}
        shadowBlur={4}
        shadowColor='#000'
        shadowOpacity={0.2}
        cornerRadius={4}
      />

      {/* Delete button */}
      <Rect
        x={x + 5}
        y={y + 5}
        width={70}
        height={20}
        fill={isHovered ? '#FF6666' : '#FF4444'}
        cornerRadius={2}
        onClick={(e) => {
          e.cancelBubble = true
          onDelete()
          onClose()
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />

      {/* Delete button text */}
      <Text
        x={x + 25}
        y={y + 9}
        text='Delete'
        fontSize={10}
        fill='#FFFFFF'
        fontFamily='Arial'
        align='center'
        listening={false}
      />
    </Group>
  )
}

export interface BaseComponentProps<T> {
  scale: number
  thicknessMultiplier?: number
  element: T
  onDelete?: (elementId: string) => void
  onSelect?: (element: T) => void
  onUpdate?: (
    elementId: string,
    elementType: string,
    updates: Partial<T>,
  ) => void
  isSelected?: boolean
  isHovered?: boolean
  isHidden?: boolean
  onResizeStart?: (
    elementId: string,
    elementType: string,
    mode: ResizeMode,
    startPos: Point2D,
    bounds: any,
  ) => void
}

interface BaseElementProps<T extends BaseElementData> {
  element: T
  scale: number
  thicknessMultiplier?: number
  isSelected?: boolean
  isHovered?: boolean
  callbacks?: ElementInteractionCallbacks<T>
}

interface BaseElementWrapperProps<T extends BaseElementData>
  extends BaseElementProps<T> {
  bounds: BaseElementBounds
  children: (props: {
    styling: ElementStyling
    behaviors: ReturnType<typeof useElementBehaviors>
    isSelected: boolean
    isHovered: boolean
    dragProps: {
      draggable: boolean
      onDragEnd: (e: KonvaEventObject<DragEvent>) => void
    }
  }) => React.ReactNode
  customColors?: {
    normal?: string
    selected?: string
    hover?: string
  }
  elementType: string // Add this to identify element type
}

export const BaseElementWrapper = <T extends BaseElementData>({
  element,
  bounds,
  scale,
  isSelected = false,
  isHovered = false,
  callbacks,
  children,
  customColors,
  elementType,
}: BaseElementWrapperProps<T>) => {
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    visible: boolean
  }>({
    x: 0,
    y: 0,
    visible: false
  });
  
  const groupRef = useRef<any>(null);
  
  // Enhanced behaviors with move functionality
  const enhancedCallbacks = {
    ...callbacks,
    onSelect: useCallback(
      (element: T) => {
        setContextMenu((prev) => ({ ...prev, visible: false })) // Hide context menu on select
        callbacks?.onSelect?.(element)
      },
      [callbacks],
    ),
  }

  const behaviors = useElementBehaviors(element, bounds, enhancedCallbacks)
  const styling = useElementStyling(
    isSelected,
    behaviors.isHovered || isHovered,
    customColors,
  )

  const handleDragEnd = useCallback(
    (e: KonvaEventObject<DragEvent>) => {
      if (!isSelected || !callbacks?.onUpdate) return

      const node = e.target
      const newX = node.x()
      const newY = node.y()

      // Calculate new center position in world coordinates
      const newCenterX = newX / scale
      const newCenterY = newY / scale

      // Update element position based on element type
      let updates: any = {}

      if ('center_point' in element) {
        if (Array.isArray((element as any).center_point)) {
          // For walls and similar elements with array center_point
          updates = {
            center_point: [newCenterX, newCenterY],
          }
        } else {
          // For beams, posts, etc. with object center_point
          updates = {
            center_point: {
              x: newCenterX,
              y: newCenterY,
            },
          }
        }
      }
      if (Object.keys(updates).length > 0) {
        callbacks.onUpdate(element.id, element.type, updates)
      }
    },
    [isSelected, element, scale, callbacks],
  )

  const handleRightClick = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      e.evt.preventDefault()
      e.cancelBubble = true
      const stage = e.target.getStage()
      const pointer = stage?.getPointerPosition()
      if (pointer) {
        setContextMenu({
          x: pointer.x,
          y: pointer.y,
          visible: true,
        })
      }
    },
    [elementType, element.id],
  )

  const dragProps = {
    draggable: isSelected,
    onDragEnd: handleDragEnd,
    onContextMenu: handleRightClick, // Add context menu to drag props
  }

  // Handle delete
  const handleDelete = useCallback(() => {
    callbacks?.onDelete?.(element.id, elementType)
  }, [element.id, elementType, callbacks])

  const closeContextMenu = useCallback(() => {
    setContextMenu((prev) => ({ ...prev, visible: false }))
  }, [])

  return (
    <Group ref={groupRef}>
      <Group>
        {children({
          styling,
          behaviors: {
            ...behaviors,
            handleClick: useCallback(
              (e: KonvaEventObject<MouseEvent>) => {
                e.cancelBubble = true
                setContextMenu((prev) => ({ ...prev, visible: false }))
                behaviors.handleClick(e)
              },
              [behaviors],
            ),
          },
          isSelected,
          isHovered: behaviors.isHovered,
          dragProps,
        })}
      </Group>

      {/* Context menu */}
      <ContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        isVisible={contextMenu.visible}
        onDelete={handleDelete}
        onClose={closeContextMenu}
      />
    </Group>
  )
}
